// Waytide chrome backdrop: noise overlay + cursor-reactive grid
// canvas. Rendered as pinned layers behind the page content so every
// surface inherits the SS-style atmospheric treatment without each
// page knowing the details.
//
// Height contract: AppBackground is intentionally height-neutral —
// the chrome layers (noise + canvas) are `fixed inset-0` so they
// cover the viewport regardless of document height. Each page
// declares its own height:
//   - fixed-viewport pages (WorkspaceRoomPage, SignInPage,
//     CodeGate, StatusPage) set `h-screen` (and overflow-hidden
//     when they manage their own internal scroll regions).
//   - scrollable pages (SettingsPage, HomePage, PublicFindingsPage,
//     LandingPage) set `min-h-screen` so the document scrolls when
//     content exceeds the viewport.
// Adding `min-h-screen` here would fight fixed-viewport pages that
// also try to clamp their height, so we leave it off.
//
// Usage: wrap a page root with <AppBackground> at the app shell.

import { useTheme } from "../theme/ThemeContext";
import { useGridCanvas } from "./GridCanvas";

export function AppBackground({ children }: { children: React.ReactNode }) {
  const { theme } = useTheme();
  const { canvasRef, handleMouseMove, handleMouseLeave } = useGridCanvas(theme);
  return (
    <div
      className="relative"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div
        className="pointer-events-none fixed inset-0 z-0 opacity-[0.035]"
        style={{
          backgroundImage:
            "url('data:image/svg+xml,%3Csvg viewBox=\"0 0 200 200\" xmlns=\"http://www.w3.org/2000/svg\"%3E%3Cfilter id=\"n\"%3E%3CfeTurbulence type=\"fractalNoise\" baseFrequency=\"1.5\" numOctaves=\"3\" stitchTiles=\"stitch\"/%3E%3C/filter%3E%3Crect width=\"100%25\" height=\"100%25\" filter=\"url(%23n)\"/%3E%3C/svg%3E')",
        }}
        aria-hidden
      />
      <canvas
        ref={canvasRef}
        className="pointer-events-none fixed inset-0 z-0 h-full w-full"
        aria-hidden
      />
      <div className="relative z-10">{children}</div>
    </div>
  );
}
